import React from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import { Tooltip } from '../../../../components/ui/Tooltip';
import { levelLabel, statusLabel } from '../utils';
import type { Goal } from '../types';

// Clarity chip color: null → neutral, >=80 green, >=50 amber, else red.
const clarityColor = (score?: number | null): string => {
  if (score == null) return 'bg-muted text-muted-foreground';
  if (score >= 80) return 'bg-success/10 text-success';
  if (score >= 50) return 'bg-warning/10 text-warning';
  return 'bg-destructive/10 text-destructive';
};

interface GoalNodeProps {
  goal: Goal;
  level?: Goal['level'];
  onNodeClick?: (goal: Goal) => void;
  isConnected?: boolean;
  // Set by the map's roving tabindex so only the active node is in the tab order.
  tabIndex?: number;
  nodeRef?: React.Ref<HTMLButtonElement>;
  // Tree semantics from the map, which must land on the focused element to be announced.
  ariaProps?: React.AriaAttributes & { role?: string };
}

const GoalNode: React.FC<GoalNodeProps> = ({
  goal,
  level = 'individual',
  onNodeClick = () => {},
  isConnected = false,
  tabIndex = 0,
  nodeRef,
  ariaProps,
}) => {
  const { t } = useTranslation();

  const getLevelStyles = () => {
    switch (level) {
      case 'company':
        return {
          bg: 'linear-gradient(135deg, var(--color-primary) 0%, #8b5cf6 100%)',
          textColor: 'text-white',
          isWhiteText: true,
          width: 360,
          height: 120,
          fontSize: 'text-lg',
          fontWeight: 'font-semibold',
          shadow: 'shadow-lg',
        };
      case 'team':
        return {
          bg: 'linear-gradient(135deg, #f59e0b 0%, #eab308 100%)',
          textColor: 'text-white',
          isWhiteText: true,
          width: 320,
          height: 100,
          fontSize: 'text-base',
          fontWeight: 'font-medium',
          shadow: 'shadow-md',
        };
      case 'individual':
      default:
        return {
          bg: 'var(--color-card)',
          textColor: 'text-foreground',
          isWhiteText: false,
          width: 280,
          height: 88,
          fontSize: 'text-sm',
          fontWeight: 'font-medium',
          shadow: 'shadow-sm',
        };
    }
  };

  const getStatusIcon = () => {
    switch (goal.status) {
      case 'completed': return 'CheckCircle';
      case 'on-track': return 'Target';
      case 'at-risk': return 'AlertTriangle';
      case 'overdue': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusColor = () => {
    switch (goal.status) {
      case 'completed': return 'var(--color-success)';
      case 'on-track': return 'var(--color-primary)';
      case 'at-risk': return 'var(--color-warning)';
      case 'overdue': return 'var(--color-error)';
      default: return 'var(--color-muted-foreground)';
    }
  };

  const styles = getLevelStyles();

  // The paged map payload carries counts instead of milestone rows, so the array is often empty.
  const msTotal = goal.milestoneTotalCount ?? goal.milestones?.length ?? 0;
  const msDone = goal.milestoneCompletedCount ?? goal.milestones?.filter(m => m.completed).length ?? 0;

  const details = goal.description ? (
    <div>
      <p className="font-medium text-foreground mb-1">{goal.title}</p>
      <p className="text-muted-foreground">{goal.description}</p>
    </div>
  ) : null;

  const nodeLabel = t('goals.map.nodeLabel', {
    title: goal.title,
    level: levelLabel(t, level),
    status: statusLabel(t, goal.status),
    progress: goal.progress,
  });

  // The tooltip only wraps the button when there is content, so this flex row keeps the node's
  // box identical either way.
  return (
    <span className="flex w-full">
      <Tooltip content={details} className="w-full">
        <button
          type="button"
          ref={nodeRef}
          tabIndex={tabIndex}
          {...ariaProps}
          aria-label={ariaProps?.['aria-label'] ?? nodeLabel}
          className={`group w-full text-start rounded-lg p-4 cursor-pointer transition-all duration-200 overflow-hidden flex flex-col justify-between ${styles.shadow} ${
            level === 'individual' ? 'border-2 border-border' : ''
          } ${isConnected ? 'ring-2 ring-primary/50' : ''} hover:scale-[1.03] focus-visible:scale-[1.03] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] focus-visible:shadow-[0_8px_24px_rgba(0,0,0,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
          style={{
            background: styles.bg,
            minHeight: styles.height,
          }}
          onClick={() => onNodeClick(goal)}
        >
          <div className="flex justify-between items-start w-full">
            <div className="flex-1 me-2 overflow-hidden">
              {/* CO-816 — the map canvas is forced `dir="ltr"` (index.tsx) so its absolutely-positioned
                  nodes stop mis-scrolling in RTL; each node wrapper then takes the UI direction back
                  (review fix). `dir="auto"` stays for MIXED-language titles, e.g. an English goal
                  title in an Arabic session, so the text itself decides its reading order. */}
              <p
                dir="auto"
                className={`${styles.fontSize} ${styles.fontWeight} truncate mb-0.5 ${styles.textColor}`}
              >
                {goal.title}
              </p>
              <div className="flex items-center gap-1">
                <AppIcon
                  name={getStatusIcon()}
                  size={14}
                  color={styles.isWhiteText ? 'rgba(255, 255, 255, 0.9)' : getStatusColor()}
                />
                <span
                  className="inline-flex items-center px-1 h-5 rounded text-[0.65rem] font-semibold uppercase"
                  style={{
                    backgroundColor: styles.isWhiteText
                      ? 'rgba(255, 255, 255, 0.2)'
                      : `${getStatusColor()}18`,
                    color: styles.isWhiteText ? 'inherit' : getStatusColor(),
                  }}
                >
                  {statusLabel(t, goal.status)}
                </span>
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.65rem] font-medium ${
                    styles.isWhiteText ? '' : clarityColor(goal.clarityScore)
                  }`}
                  style={
                    styles.isWhiteText
                      ? { backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'inherit' }
                      : undefined
                  }
                  title={t('goals.ai.clarityTooltip')}
                  data-testid={`goal-clarity-${goal.id}`}
                >
                  {/* CO-915: labelled, so the clarity score cannot read as goal progress beside the status. */}
                  {t('Clarity {{score}}', { score: goal.clarityScore == null ? '-' : `${goal.clarityScore}%` })}
                </span>
                {msTotal > 0 && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[0.6rem] font-semibold"
                    style={{
                      backgroundColor: styles.isWhiteText ? 'rgba(255, 255, 255, 0.2)' : 'color-mix(in srgb, var(--color-primary) 15%, transparent)',
                      color: styles.isWhiteText ? 'inherit' : 'var(--color-primary)',
                    }}
                    title={t('{{completed}}/{{total}} milestones completed', { completed: msDone, total: msTotal })}
                    data-testid={`milestone-indicator-${goal.id}`}
                  >
                    <AppIcon
                      name="Flag"
                      size={10}
                      color={styles.isWhiteText ? 'rgba(255, 255, 255, 0.9)' : 'var(--color-primary)'}
                    />
                    {msDone}/{msTotal}
                  </span>
                )}
                {goal.hasActiveBlockers && (
                  <AppIcon
                    name="AlertOctagon"
                    size={14}
                    color="var(--color-error)"
                    title={t('{{count}} active blocker', { count: goal.activeBlockerCount || 0 })}
                  />
                )}
              </div>
              {goal.owner && (
                <span
                  dir="auto"
                  className={`block text-[0.7rem] mt-0.5 truncate ${
                    styles.isWhiteText ? 'opacity-75' : 'text-muted-foreground opacity-75'
                  }`}
                >
                  {goal.owner}
                </span>
              )}
            </div>
            <AppIcon
              name="ChevronRight"
              size={16}
              color={styles.isWhiteText ? 'rgba(255, 255, 255, 0.6)' : 'var(--color-muted-foreground)'}
              className="transition-transform rtl:rotate-180 group-hover:translate-x-1 group-focus-visible:translate-x-1 rtl:group-hover:-translate-x-1 rtl:group-focus-visible:-translate-x-1"
            />
          </div>

          <div className="w-full">
            <div className="flex justify-between mb-0.5">
              <span
                className={`text-[0.7rem] ${
                  styles.isWhiteText ? 'opacity-80' : 'text-muted-foreground opacity-70'
                }`}
              >
                {goal.progress}%
              </span>
            </div>
            <div
              className="h-[5px] rounded-full overflow-hidden"
              style={{
                backgroundColor: styles.isWhiteText ? 'rgba(255,255,255,0.2)' : 'var(--color-muted)',
              }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${goal.progress}%`,
                  backgroundColor: styles.isWhiteText ? 'white' : getStatusColor(),
                }}
              />
            </div>
          </div>
        </button>
      </Tooltip>
    </span>
  );
};

export default GoalNode;
